import * as React from 'react';
import { useState, useEffect } from 'react';
import { Text, UnorderedList, ListItem } from '@chakra-ui/react';

import {
  TextareaWithConfirm,
  UploadActions,
  UploadButtonSection,
  UploadComplete as UploadCompleteSection,
  Modal,
  AcknowledgeBox,
  RequirementsSummary,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import { EmploymentStatus } from '../../../../../store/student/financeDocuments/types';

interface IProps {
  sectionTitle: string;
  fileNames: string[];
  complete: boolean;
  onClose: () => void;
  onContinue: () => void;
  onUpload: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  leadSponsor: string;
  currentEmploymentStatus?: EmploymentStatus;
  fundSource?: string;
  onAcknowledgeToggle: (status: string) => void;
  onSaveMetaDataForNoDoc: (metadata?: any) => void;
  isSupportingEvidenceProvided: boolean;
}

const EmploymentReferenceModal: React.FC<IProps> = (props: IProps) => {
  const { complete, infoOnly, docStatus, docKey, sectionTitle, leadSponsor, isSupportingEvidenceProvided } = props;

  const [currentEmploymentStatus, setCurrentEmploymentStatus] = useState<EmploymentStatus | undefined>(
    props.currentEmploymentStatus
  );

  const [fundSource, setFundSource] = useState<string | undefined>(props.fundSource);

  const hasNoRecentEmploymentHistory = [
    EmploymentStatus.OVER_5_YRS_EMP,
    EmploymentStatus.NEVER_EMP,
    EmploymentStatus.RETIED
  ].includes(currentEmploymentStatus!);

  const hasRecentEmploymentHistory = (status: EmploymentStatus) => {
    return [EmploymentStatus.SELF_EMP, EmploymentStatus.WITHIN_5_YRS_EMP, EmploymentStatus.COMPANY_EMP].includes(
      status
    );
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const onContinue = () => {
    if (hasNoRecentEmploymentHistory && !isSupportingEvidenceProvided) {
      props.onSaveMetaDataForNoDoc({
        currentEmploymentStatus: currentEmploymentStatus,
        fundSource: fundSource
      });
    }
    props.onContinue();
  };

  useEffect(() => {
    if (hasRecentEmploymentHistory(currentEmploymentStatus!)) {
      props.onSaveMetaDataForNoDoc({
        currentEmploymentStatus: currentEmploymentStatus,
        fundSource: ''
      });
      setFundSource('');
    }
    if (hasNoRecentEmploymentHistory) {
      props.onSaveMetaDataForNoDoc({
        currentEmploymentStatus: currentEmploymentStatus,
        fundSource: fundSource
      });
    }
  }, [currentEmploymentStatus]);

  return (
    <Modal id={'employmentReference'} show={true} title={sectionTitle} onClose={handleOnClose} hideFooter>
      {infoOnly ? (
        <>
          <Text>
            During your visa application you may be required to provide a reference letter from your{' '}
            {leadSponsor !== 'student' ? `${leadSponsor}'s ` : ' '}
            current or recent employer.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            label="Return to document upload"
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
          />
        </>
      ) : (
        <>
          {complete && hasRecentEmploymentHistory(currentEmploymentStatus!) ? (
            <UploadCompleteSection
              fileNames={props.fileNames}
              onUpload={() => {
                if (hasRecentEmploymentHistory(currentEmploymentStatus!)) {
                  props.onSaveMetaDataForNoDoc({
                    currentEmploymentStatus: currentEmploymentStatus
                  });
                }
                props.onUpload();
              }}
              onContinue={onContinue}
            />
          ) : (
            <>
              <Text>
                We require a reference letter from your {leadSponsor !== 'student' ? `${leadSponsor}'s ` : ' '}
                current or recent employer.
              </Text>

              <RadioOptionsWithConfirm
                label={`What is your ${
                  leadSponsor !== 'student' ? `${leadSponsor}'s ` : ' '
                }current employment status?`}
                questions={[
                  {
                    char: '1',
                    label: EmploymentStatus.COMPANY_EMP,
                    selected: currentEmploymentStatus === EmploymentStatus.COMPANY_EMP
                  },
                  {
                    char: '2',
                    label: EmploymentStatus.SELF_EMP,
                    selected: currentEmploymentStatus === EmploymentStatus.SELF_EMP
                  },
                  {
                    char: '3',
                    label: EmploymentStatus.WITHIN_5_YRS_EMP,
                    selected: currentEmploymentStatus === EmploymentStatus.WITHIN_5_YRS_EMP
                  },
                  {
                    char: '4',
                    label: EmploymentStatus.OVER_5_YRS_EMP,
                    selected: currentEmploymentStatus === EmploymentStatus.OVER_5_YRS_EMP
                  },
                  {
                    char: '5',
                    label: EmploymentStatus.NEVER_EMP,
                    selected: currentEmploymentStatus === EmploymentStatus.NEVER_EMP
                  },
                  {
                    char: '6',
                    label: EmploymentStatus.RETIED,
                    selected: currentEmploymentStatus === EmploymentStatus.RETIED
                  }
                ]}
                onClick={(question: IQuestion) => {
                  if (!isSupportingEvidenceProvided) {
                    setCurrentEmploymentStatus(question.label as EmploymentStatus);
                  }
                }}
              />

              {currentEmploymentStatus === EmploymentStatus.SELF_EMP ? (
                <>
                  <RequirementsSummary title="Please ensure the document:">
                    <UnorderedList>
                      <ListItem>Is on company letterhead</ListItem>
                      <ListItem>
                        Includes details of the self-employment including nature of the work and salary
                      </ListItem>
                    </UnorderedList>
                  </RequirementsSummary>

                  <UploadButtonSection
                    onUpload={() => {
                      if (hasRecentEmploymentHistory(currentEmploymentStatus!)) {
                        props.onSaveMetaDataForNoDoc({
                          currentEmploymentStatus: currentEmploymentStatus
                        });
                      }
                      props.onUpload();
                    }}
                  >
                    Awaiting upload of <b>Employment Reference</b>
                  </UploadButtonSection>
                </>
              ) : null}

              {[EmploymentStatus.WITHIN_5_YRS_EMP, EmploymentStatus.COMPANY_EMP].includes(currentEmploymentStatus!) ? (
                <>
                  <RequirementsSummary title="Please ensure the document:">
                    <UnorderedList>
                      <ListItem>Confirms the term of the employment</ListItem>
                      <ListItem>
                        Matches your {leadSponsor !== 'student' ? `${leadSponsor}'s ` : ' '}
                        name as per your supplied finance documents
                      </ListItem>
                    </UnorderedList>
                  </RequirementsSummary>

                  <UploadButtonSection
                    onUpload={() => {
                      if (hasRecentEmploymentHistory(currentEmploymentStatus!)) {
                        props.onSaveMetaDataForNoDoc({
                          currentEmploymentStatus: currentEmploymentStatus
                        });
                      }
                      props.onUpload();
                    }}
                  >
                    Awaiting upload of <b>Employment Reference</b>
                  </UploadButtonSection>
                </>
              ) : null}

              {hasNoRecentEmploymentHistory ? (
                <>
                  <TextareaWithConfirm
                    key={'fund-source'}
                    id={'fund-source'}
                    value={fundSource}
                    label={`Please explain how the funds for your study came to be in your ${
                      leadSponsor !== 'student' ? `${leadSponsor}'s ` : ' '
                    }account despite no recent employment history`}
                    onChange={(evt: React.FormEvent<HTMLInputElement>) => {
                      setFundSource(evt.currentTarget.value);
                    }}
                  />

                  <RequirementsRemedy isSatisfied>
                    This item will be marked as Done. You may be contacted for more information.
                  </RequirementsRemedy>
                </>
              ) : null}

              <UploadActions complete={hasNoRecentEmploymentHistory} onContinue={onContinue} />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default EmploymentReferenceModal;
